// src/views/Catalog/CreateSchoolCatalog.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import useVendorBranch from '../../utils/useVendorBranch';

export default function CreateSchoolCatalog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch } = useVendorBranch(user);

  // ✅ vendor/branch selection (same pattern)
  const [selectionData, setSelectionData] = useState({
    vendor_id: user?.vendor_id ?? 0,
    branch_id: user?.branch_id ?? 0,

    // ✅ add names (for payload; selector may also set these)
    vendor_name: user?.vendor_name ?? user?.VendorName ?? '',
    branch_name: user?.branch_name ?? user?.BranchName ?? '',
  });

  const [errorMessage, setErrorMessage] = useState({});

  // ✅ UI state (NO DESIGN CHANGE)
  const [catalog, setCatalog] = useState({
    catalogId: 0,
    catalogName: 'Spring Term 2025 Prospectus',
    catalogYear: 2025,
    catalogDate: new Date().toISOString().split('T')[0],
    isActive: true,
    sections: [
      {
        sectionId: 0,
        sectionName: 'About School',
        sectionOrder: 1,
        isDefault: true, // ✅ kept in data (no UI)
        isRepeating: false, // ✅ kept in data (no UI)
        subSections: [
          {
            subSectionId: 0,
            subSectionName: "Principal's Message",
            gridSystem: 'no-division',
            alignment: 'left',
            contentType: '',
            isRepeating: false, // ✅ kept in data (no UI)
            allowDynamicData: false,
            dataSourceTable: '',
            dataSourceQuery: '',
            subSectionOrder: 1,
            contents: [
              {
                contentId: 0,
                contentType: 'Heading',
                contentValue: 'Welcome to Our School',
                contentOrder: 1,
                gridPosition: '',
                styleJson: '',
                isDynamic: false,
                dataSourceTable: '',
                dataSourceQuery: '',
              },
              {
                contentId: 0,
                contentType: 'Text',
                contentValue: 'Our mission is to nurture excellence...',
                contentOrder: 2,
                gridPosition: '',
                styleJson: '',
                isDynamic: false,
                dataSourceTable: '',
                dataSourceQuery: '',
              },
            ],
          },
        ],
      },
    ],
  });

  const toYMD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toISOString().split('T')[0];
  };

  const mapApiToUi = (apiData) => {
    // api can be:
    // 1) { catalog: Catalog_Master, sections: [], subSections: [], catalogContents: [] }
    // 2) Catalog_Master with navigation: Sections -> SubSections -> CatalogContents
    const master = apiData?.catalog ?? apiData ?? {};
    const sections =
      apiData?.sections ??
      master?.Sections ??
      master?.sections ??
      master?.Sections ??
      [];

    const hasNested =
      (sections?.[0]?.SubSections || sections?.[0]?.subSections) != null;

    if (hasNested) {
      return {
        catalogId: master.catalog_id ?? 0,
        catalogName: master.catalog_name ?? '',
        catalogYear: master.catalog_year ?? new Date().getFullYear(),
        catalogDate: toYMD(master.catalog_date) || toYMD(new Date()),
        isActive: !!master.is_active,
        sections: (sections || []).map((s, si) => ({
          sectionId: s.section_id ?? 0,
          sectionName: s.section_name ?? '',
          sectionOrder: s.section_order ?? si + 1,
          isDefault: !!s.is_default,
          isRepeating: !!s.is_repeating,
          subSections: (s.SubSections ?? s.subSections ?? []).map(
            (ss, ssi) => ({
              subSectionId: ss.sub_section_id ?? 0,
              subSectionName: ss.sub_section_name ?? '',
              gridSystem: ss.grid_system ?? 'no-division',
              alignment: ss.alignment ?? 'left',
              contentType: ss.content_type ?? '',
              isRepeating: !!ss.is_repeating,
              allowDynamicData: !!ss.allow_dynamic_data,
              dataSourceTable: ss.data_source_table ?? '',
              dataSourceQuery: ss.data_source_query ?? '',
              subSectionOrder: ss.sub_section_order ?? ssi + 1,
              contents: (
                ss.CatalogContents ??
                ss.catalogContents ??
                ss.contents ??
                []
              ).map((c, ci) => ({
                contentId: c.content_id ?? 0,
                contentType: c.content_type ?? 'Text',
                contentValue: c.content_value ?? '',
                contentOrder: c.content_order ?? ci + 1,
                gridPosition: c.grid_position ?? '',
                styleJson: c.style_json ?? '',
                isDynamic: !!c.is_dynamic,
                dataSourceTable: c.data_source_table ?? '',
                dataSourceQuery: c.data_source_query ?? '',
              })),
            })
          ),
        })),
      };
    }

    // flat lists
    const flatSections = apiData?.sections ?? [];
    const flatSubSections = apiData?.subSections ?? [];
    const flatContents =
      apiData?.catalogContents ?? apiData?.CatalogContents ?? [];

    return {
      catalogId: master.catalog_id ?? 0,
      catalogName: master.catalog_name ?? '',
      catalogYear: master.catalog_year ?? new Date().getFullYear(),
      catalogDate: toYMD(master.catalog_date) || toYMD(new Date()),
      isActive: !!master.is_active,
      sections: (flatSections || []).map((s, si) => {
        const mySubs = (flatSubSections || []).filter(
          (x) => Number(x.section_id) === Number(s.section_id)
        );

        return {
          sectionId: s.section_id ?? 0,
          sectionName: s.section_name ?? '',
          sectionOrder: s.section_order ?? si + 1,
          isDefault: !!s.is_default,
          isRepeating: !!s.is_repeating,
          subSections: mySubs.map((ss, ssi) => {
            const myContents = (flatContents || []).filter(
              (c) => Number(c.sub_section_id) === Number(ss.sub_section_id)
            );

            return {
              subSectionId: ss.sub_section_id ?? 0,
              subSectionName: ss.sub_section_name ?? '',
              gridSystem: ss.grid_system ?? 'no-division',
              alignment: ss.alignment ?? 'left',
              contentType: ss.content_type ?? '',
              isRepeating: !!ss.is_repeating,
              allowDynamicData: !!ss.allow_dynamic_data,
              dataSourceTable: ss.data_source_table ?? '',
              dataSourceQuery: ss.data_source_query ?? '',
              subSectionOrder: ss.sub_section_order ?? ssi + 1,
              contents: myContents.map((c, ci) => ({
                contentId: c.content_id ?? 0,
                contentType: c.content_type ?? 'Text',
                contentValue: c.content_value ?? '',
                contentOrder: c.content_order ?? ci + 1,
                gridPosition: c.grid_position ?? '',
                styleJson: c.style_json ?? '',
                isDynamic: !!c.is_dynamic,
                dataSourceTable: c.data_source_table ?? '',
                dataSourceQuery: c.data_source_query ?? '',
              })),
            };
          }),
        };
      }),
    };
  };

  const buildPayload = () => {
    const now = new Date().toISOString();

    const master = {
      catalog_id: Number(catalog.catalogId || 0),
      catalog_name: catalog.catalogName || '',
      catalog_year: catalog.catalogYear ? Number(catalog.catalogYear) : null,
      catalog_date: catalog.catalogDate || null,
      created_by: user?.id ?? null,
      created_on: now,
      is_active: !!catalog.isActive,
      created_at: now,
      updated_at: now,
      vendor_id: selectionData.vendor_id
        ? Number(selectionData.vendor_id)
        : null,
      branch_id: selectionData.branch_id
        ? Number(selectionData.branch_id)
        : null,
      user_id: user?.id ?? null,
      user_name: user?.user_name ?? user?.name ?? null,

      // extra names
      vendor_name: selectionData.vendor_name ?? '',
      branch_name: selectionData.branch_name ?? '',
    };

    const sections = (catalog.sections || []).map((s, si) => {
      const sectionId = Number(s.sectionId || 0);

      return {
        section_id: sectionId,
        catalog_id: master.catalog_id || 0,
        section_name: s.sectionName ?? '',
        section_order: s.sectionOrder ?? si + 1,
        is_default: !!s.isDefault,
        is_repeating: !!s.isRepeating,
        created_on: now,
        created_at: now,
        updated_at: now,
        vendor_id: master.vendor_id,
        branch_id: master.branch_id,
        user_id: master.user_id,
        user_name: master.user_name,

        // 👇 NESTED SUBSECTIONS
        subSections: (s.subSections || []).map((ss, ssi) => {
          const subSectionId = Number(ss.subSectionId || 0);

          return {
            sub_section_id: subSectionId,
            section_id: sectionId || 0,
            sub_section_name: ss.subSectionName ?? '',
            is_repeating: !!ss.isRepeating,
            grid_system: ss.gridSystem ?? '',
            alignment: ss.alignment ?? '',
            content_type: ss.contentType ?? '',
            allow_dynamic_data: !!ss.allowDynamicData,
            data_source_table: ss.dataSourceTable ?? '',
            data_source_query: ss.dataSourceQuery ?? '',
            sub_section_order: ss.subSectionOrder ?? ssi + 1,
            created_on: now,
            created_at: now,
            updated_at: now,
            vendor_id: master.vendor_id,
            branch_id: master.branch_id,
            user_id: master.user_id,
            user_name: master.user_name,

            // 👇 NESTED CONTENTS
            catalogContents: (ss.contents || []).map((c, ci) => ({
              content_id: Number(c.contentId || 0),
              sub_section_id: subSectionId || 0,
              content_type: c.contentType ?? '',
              content_value: c.contentValue ?? '',
              content_order: c.contentOrder ?? ci + 1,
              grid_position: c.gridPosition ?? '',
              style_json: c.styleJson ?? '',
              is_dynamic: !!c.isDynamic,
              data_source_table: c.dataSourceTable ?? '',
              data_source_query: c.dataSourceQuery ?? '',
              created_on: now,
              updated_at: now,
              vendor_id: master.vendor_id,
              branch_id: master.branch_id,
              user_id: master.user_id,
              user_name: master.user_name,
            })),
          };
        }),
      };
    });

    return {
      catalog: master,
      sections,
    };
  };

  // ---------------------------
  // Edit mode (load)
  // ---------------------------
  useEffect(() => {
    // if listing page passes full record
    if (location?.state?.catalog) {
      const ui = mapApiToUi(location.state);
      setCatalog((prev) => ({ ...prev, ...ui }));

      // ✅ selection (also keep names if present)
      setSelectionData((prev) => ({
        ...prev,
        vendor_id: location.state.catalog.vendor_id ?? prev.vendor_id,
        branch_id: location.state.catalog.branch_id ?? prev.branch_id,
        vendor_name:
          location.state.catalog.vendor_name ??
          location.state.catalog.vendorName ??
          prev.vendor_name,
        branch_name:
          location.state.catalog.branch_name ??
          location.state.catalog.branchName ??
          prev.branch_name,
      }));
      return;
    }

    const id =
      location?.state?.catalog_id ??
      location?.state?.catalogId ??
      location?.state?.id ??
      0;

    const vendor_id = location?.state?.vendor_id ?? 0;
    const branch_id = location?.state?.branch_id ?? 0;
    debugger;
    if (id && Number(id) > 0) {
      (async () => {
        try {
          const res = await apiService.get(
            `api/Catalog/GetCatalogById?id=${id}&vendorId=${vendor_id}&branchId=${branch_id}`
          );
          debugger;
          if (res?.status === true) {
            const ui = mapApiToUi(res.data);
            setCatalog((prev) => ({ ...prev, ...ui }));

            setSelectionData((prev) => ({
              ...prev,
              vendor_id:
                res?.data?.catalog?.vendor_id ??
                res?.data?.vendor_id ??
                prev.vendor_id,
              branch_id:
                res?.data?.catalog?.branch_id ??
                res?.data?.branch_id ??
                prev.branch_id,

              // ✅ names from api if your api returns
              vendor_name:
                res?.data?.catalog?.vendor_name ??
                res?.data?.vendor_name ??
                prev.vendor_name,
              branch_name:
                res?.data?.catalog?.branch_name ??
                res?.data?.branch_name ??
                prev.branch_name,
            }));
          } else {
            toast.error(res?.message || 'Failed to fetch catalog');
          }
        } catch (err) {
          console.error(err);
          toast.error('Something went wrong');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state]);

  // ---------------------------
  // CRUD actions
  // ---------------------------
  const validate = () => {
    if (
      isSuperAdmin &&
      (!selectionData.vendor_id || Number(selectionData.vendor_id) <= 0)
    ) {
      toast.error('Please select Vendor');
      return false;
    }

    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!selectionData.branch_id || Number(selectionData.branch_id) <= 0)
    ) {
      toast.error('Please select Branch');
      return false;
    }

    if (!catalog.catalogName?.trim()) {
      toast.error('Catalog Name required');
      return false;
    }
    return true;
  };

  const saveCatalog = async () => {
    if (!validate()) return;

    try {
      const payload = buildPayload();

      let res;
      const id = Number(payload.catalog?.catalog_id || 0);

      console.log(payload, 'payload');
      if (id > 0) {
        // ✅ update pattern (same as weekend)
        res = await apiService.put(
          `api/Catalog/UpdateCatalog?id=${id}`,
          payload
        );
      } else {
        // ✅ add pattern
        res = await apiService.post('api/Catalog/AddCatalog', payload);
      }
      console.log(res);
      if (res?.status === true) {
        toast.success(res.message || 'Saved successfully');
      } else {
        toast.error(res?.message || 'Failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  // ---------------------------
  // UI handlers (NO DESIGN CHANGE)
  // ---------------------------
  const handleCatalogChange = (e) => {
    const { name, value } = e.target;
    setCatalog({ ...catalog, [name]: value });
    setErrorMessage((prev) => ({ ...prev, [name]: '' }));
  };

  const addSection = () => {
    setCatalog({
      ...catalog,
      sections: [
        ...catalog.sections,
        {
          sectionId: 0,
          sectionName: '',
          sectionOrder: catalog.sections.length + 1,
          isDefault: false, // ✅ no UI
          isRepeating: false, // ✅ no UI
          subSections: [],
        },
      ],
    });
  };

  const removeSection = (sectionIndex) => {
    const updatedSections = catalog.sections.filter(
      (_, i) => i !== sectionIndex
    );
    setCatalog({ ...catalog, sections: updatedSections });
  };

  const addSubSection = (sectionIndex) => {
    const updatedSections = [...catalog.sections];
    updatedSections[sectionIndex].subSections.push({
      subSectionId: 0,
      subSectionName: '',
      gridSystem: 'no-division',
      alignment: 'left',
      contentType: '',
      isRepeating: false, // ✅ no UI
      allowDynamicData: false,
      dataSourceTable: '',
      dataSourceQuery: '',
      subSectionOrder: updatedSections[sectionIndex].subSections.length + 1,
      contents: [],
    });
    setCatalog({ ...catalog, sections: updatedSections });
  };

  const removeSubSection = (sectionIndex, subIndex) => {
    const updatedSections = [...catalog.sections];
    updatedSections[sectionIndex].subSections = updatedSections[
      sectionIndex
    ].subSections.filter((_, i) => i !== subIndex);
    setCatalog({ ...catalog, sections: updatedSections });
  };

  const addContent = (sectionIndex, subIndex) => {
    const updatedSections = [...catalog.sections];
    updatedSections[sectionIndex].subSections[subIndex].contents.push({
      contentId: 0,
      contentType: 'Text',
      contentValue: '',
      contentOrder:
        updatedSections[sectionIndex].subSections[subIndex].contents.length + 1,
      gridPosition: '',
      styleJson: '',
      isDynamic: false,
      dataSourceTable: '',
      dataSourceQuery: '',
    });
    setCatalog({ ...catalog, sections: updatedSections });
  };

  const removeContent = (sectionIndex, subIndex, contentIndex) => {
    const updatedSections = [...catalog.sections];
    updatedSections[sectionIndex].subSections[subIndex].contents =
      updatedSections[sectionIndex].subSections[subIndex].contents.filter(
        (_, i) => i !== contentIndex
      );
    setCatalog({ ...catalog, sections: updatedSections });
  };

  const updateContentValue = (sectionIndex, subIndex, contentIndex, value) => {
    const updatedSections = [...catalog.sections];
    updatedSections[sectionIndex].subSections[subIndex].contents[
      contentIndex
    ].contentValue = value;
    setCatalog({ ...catalog, sections: updatedSections });
  };

  return (
    <>
      <div className='text-right mb-2'>
        <Link className='btn btn-warning' to='/list-catalog'>
          <ArrowBackIcon /> Back
        </Link>
      </div>
      {/* Vendor / Branch */}
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        accessButton
        errorMessage={errorMessage}
      />

      <Box sx={{ mx: 'auto' }}>
        {/* Header Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant='h4' fontWeight='600'>
              Create School Catalog
            </Typography>
          </Box>

          {/* Basic Catalog Info */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label='Catalog Name'
                value={catalog.catalogName}
                onChange={(e) =>
                  setCatalog({ ...catalog, catalogName: e.target.value })
                }
                fullWidth
                variant='outlined'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label='Year'
                type='number'
                value={catalog.catalogYear}
                onChange={(e) =>
                  setCatalog({ ...catalog, catalogYear: e.target.value })
                }
                fullWidth
                variant='outlined'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label='Date'
                type='date'
                value={catalog.catalogDate}
                onChange={(e) =>
                  setCatalog({ ...catalog, catalogDate: e.target.value })
                }
                fullWidth
                variant='outlined'
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={catalog.isActive}
                    onChange={(e) =>
                      setCatalog({ ...catalog, isActive: e.target.checked })
                    }
                    color='primary'
                  />
                }
                label='Active Catalog'
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Sections Container */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant='h5' fontWeight='500'>
              Sections{' '}
              {catalog.sections.length > 0 && (
                <Chip
                  label={catalog.sections.length}
                  size='small'
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={addSection}
            >
              Add Section
            </Button>
          </Box>

          {catalog.sections.map((section, sIndex) => (
            <Accordion
              key={sIndex}
              sx={{ mb: 2, '&:before': { display: 'none' } }}
              elevation={3}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    pr: 2,
                  }}
                >
                  <Typography fontWeight='500'>
                    {section.sectionName || `Section ${sIndex + 1}`}
                  </Typography>
                  {section.subSections.length > 0 && (
                    <Chip
                      label={`${section.subSections.length} subsections`}
                      size='small'
                      sx={{ ml: 2, bgcolor: 'rgba(255,255,255,0.2)' }}
                    />
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={10}>
                    <TextField
                      label='Section Name'
                      value={section.sectionName}
                      onChange={(e) => {
                        const updatedSections = [...catalog.sections];
                        updatedSections[sIndex].sectionName = e.target.value;
                        setCatalog({ ...catalog, sections: updatedSections });
                      }}
                      fullWidth
                      variant='outlined'
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant='outlined'
                      color='error'
                      fullWidth
                      startIcon={<DeleteIcon />}
                      onClick={() => removeSection(sIndex)}
                      sx={{ height: '56px' }}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* SubSections */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant='h6'>SubSections</Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={() => addSubSection(sIndex)}
                    >
                      Add SubSection
                    </Button>
                  </Box>

                  {section.subSections.map((sub, ssIndex) => (
                    <Card key={ssIndex} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography
                              variant='subtitle1'
                              fontWeight='500'
                              gutterBottom
                            >
                              SubSection {ssIndex + 1}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              label='SubSection Name'
                              value={sub.subSectionName}
                              onChange={(e) => {
                                const updatedSections = [...catalog.sections];
                                updatedSections[sIndex].subSections[
                                  ssIndex
                                ].subSectionName = e.target.value;
                                setCatalog({
                                  ...catalog,
                                  sections: updatedSections,
                                });
                              }}
                              fullWidth
                              size='small'
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size='small'>
                              <InputLabel>Grid System</InputLabel>
                              <Select
                                value={sub.gridSystem}
                                label='Grid System'
                                onChange={(e) => {
                                  const updatedSections = [...catalog.sections];
                                  updatedSections[sIndex].subSections[
                                    ssIndex
                                  ].gridSystem = e.target.value;
                                  setCatalog({
                                    ...catalog,
                                    sections: updatedSections,
                                  });
                                }}
                              >
                                <MenuItem value='no-grid'>No Grid</MenuItem>
                                <MenuItem value='2-grid'>2 Grid</MenuItem>
                                <MenuItem value='3-grid'>3 Grid</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size='small'>
                              <InputLabel>Alignment</InputLabel>
                              <Select
                                value={sub.alignment}
                                label='Alignment'
                                onChange={(e) => {
                                  const updatedSections = [...catalog.sections];
                                  updatedSections[sIndex].subSections[
                                    ssIndex
                                  ].alignment = e.target.value;
                                  setCatalog({
                                    ...catalog,
                                    sections: updatedSections,
                                  });
                                }}
                              >
                                <MenuItem value='left'>Left</MenuItem>
                                <MenuItem value='center'>Center</MenuItem>
                                <MenuItem value='right'>Right</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={sub.allowDynamicData}
                                  onChange={(e) => {
                                    const updatedSections = [
                                      ...catalog.sections,
                                    ];
                                    updatedSections[sIndex].subSections[
                                      ssIndex
                                    ].allowDynamicData = e.target.checked;
                                    setCatalog({
                                      ...catalog,
                                      sections: updatedSections,
                                    });
                                  }}
                                  size='small'
                                />
                              }
                              label='Allow Dynamic Data'
                            />
                          </Grid>

                          {/* Content Items */}
                          {sub.contents.length > 0 && (
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                                Content Items
                              </Typography>
                              {sub.contents.map((c, cIndex) => (
                                <Box
                                  key={cIndex}
                                  sx={{
                                    p: 2,
                                    mb: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'white',
                                  }}
                                >
                                  <Grid
                                    container
                                    spacing={2}
                                    alignItems='center'
                                  >
                                    <Grid item xs={12} sm={4}>
                                      <FormControl fullWidth size='small'>
                                        <InputLabel>Content Type</InputLabel>
                                        <Select
                                          value={c.contentType}
                                          label='Content Type'
                                          onChange={(e) => {
                                            const updatedSections = [
                                              ...catalog.sections,
                                            ];
                                            updatedSections[sIndex].subSections[
                                              ssIndex
                                            ].contents[cIndex].contentType =
                                              e.target.value;
                                            setCatalog({
                                              ...catalog,
                                              sections: updatedSections,
                                            });
                                          }}
                                        >
                                          <MenuItem value='Heading'>
                                            Heading
                                          </MenuItem>
                                          <MenuItem value='Text'>Text</MenuItem>
                                          <MenuItem value='Image'>
                                            Image
                                          </MenuItem>
                                          <MenuItem value='Table'>
                                            Table
                                          </MenuItem>
                                          <MenuItem value='Border'>
                                            Border
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        label='Content Value'
                                        value={c.contentValue}
                                        onChange={(e) =>
                                          updateContentValue(
                                            sIndex,
                                            ssIndex,
                                            cIndex,
                                            e.target.value
                                          )
                                        }
                                        fullWidth
                                        size='small'
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                      <IconButton
                                        color='error'
                                        onClick={() =>
                                          removeContent(sIndex, ssIndex, cIndex)
                                        }
                                        size='small'
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant='outlined'
                                size='small'
                                startIcon={<AddIcon />}
                                onClick={() => addContent(sIndex, ssIndex)}
                              >
                                Add Content
                              </Button>
                              <Button
                                variant='outlined'
                                color='error'
                                size='small'
                                startIcon={<DeleteIcon />}
                                onClick={() =>
                                  removeSubSection(sIndex, ssIndex)
                                }
                              >
                                Remove SubSection
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}

                  {section.subSections.length === 0 && (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: 'text.secondary',
                      }}
                    >
                      <Typography>
                        No subsections yet. Click "Add SubSection" to create
                        one.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          {catalog.sections.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography color='text.secondary'>
                No sections created yet. Click "Add Section" to begin building
                your catalog.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Save Button */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 20,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            variant='contained'
            size='large'
            startIcon={<SaveIcon />}
            onClick={saveCatalog}
            sx={{ minWidth: 200, boxShadow: 4 }}
          >
            Save Catalog
          </Button>
        </Box>
      </Box>
    </>
  );
}
